import IRepository from "data/IRepository";
import {
  Contracting_FinishContracting,
  Contracting_UpdateContracting,
} from "data/endpoints";
import ContractingStepFormProps from "features/contracting/models/ContractingStepFormProps";
import { FormFields } from "features/shared/forms/hooks/useForm";
import { isEmpty } from "features/shared/helpers/objects";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import IAppState from "infrastructure/store/IAppState";
import {
  ContractingAction,
  TouchedActionData,
  setContractingId,
  setContractingStepDisabled,
  setContractingStepTouched,
  setContractingStepValid,
  setContractingStepValues,
} from "infrastructure/store/contracting/ContractingActions";
import ContractingState from "infrastructure/store/contracting/ContractingState";
import { Dispatch, useEffect } from "react";
import { trackPromise } from "react-promise-tracker";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ContractingDTO } from "features/contracting/dtos/ContractingDTO";
import ActionResultDTO from "features/shared/dtos/ActionResultDTO";
import PostResponse from "data/PostResponse";
import { isSuccessStatus, isBadRequestStatus } from "data/helpers/responseHelper";
import { getQueryParams } from "features/shared/helpers/queryParams";
import { useContractingDialog } from "features/contracting/hooks/useContractingDialog";
import { getInvalidLinkMessageKeyFromStateCode, getStateCodeFromValidationProblem } from "features/contracting/helpers/contractingLinkHelper";
import { AlertType } from "features/shared/alerts/enums/alertType";
import { produce } from "immer";
import { redirectToUrl } from "features/shared/helpers/urlHelper";
import UserManager from "infrastructure/oidc/UserManager";
import { ContractingDialogButtonProps } from "features/contracting/models/ContractingDialogButtonProps";
import { UserState } from "redux-oidc";

//#region models
export interface ContractingStepHookProps extends ContractingStepFormProps {
  formValues: FormFields;
  validateForm: () => boolean;
}

export interface RegistrationStepHookResult {
  onNextClick: (save?: boolean, markAsDisabled?: boolean) => void;
  onPrevClick: () => void;
  onSaveClick: () => void;
  onFinishClick: (showLoginBtn?: boolean) => void;
  onBackToLoginClick: () => void;
}
//#endregion models

export const useContractingStep = ({
  actualStep,
  nextStep,
  prevStep,
  formValues,
  validateForm,
}: ContractingStepHookProps): RegistrationStepHookResult => {
  //#region DI
  const contractingRepository = useInjection<IRepository<ContractingDTO>>(
    IocTypes.Repository
  );
  //#endregion DI

  //#region router
  const history = useHistory();
  //#endregion router

  //#region info dialog
  const { openInfoDialog, closeInfoDialog } = useContractingDialog();
  //#endregion info dialog

  //#region store
  const contractingState = useSelector<IAppState, ContractingState>(
    (store) => store.contracting
  );
  const userState = useSelector<IAppState, UserState>((store) => store.oidc);
  // dispatches
  const contractingDispatch = useDispatch<Dispatch<ContractingAction>>();
  //#endregion store

  //#endregion side-effects
  // set registration step to touched
  useEffect(() => {
    const actionData: TouchedActionData = {
      stepRoute: history.location.pathname,
      touched: true,
    };

    contractingDispatch(
      setContractingStepTouched({
        key: actualStep.component,
        value: actionData,
      })
    );
  }, []);

  // after values-change set to store
  useEffect(() => {
    if (!isEmpty(formValues)) {
      contractingDispatch(
        setContractingStepValues({
          key: actualStep.component,
          value: formValues,
        })
      );
    }
  }, [formValues]);

  //#endregion side-effects

  //#region handlers
  const nextClickHandler = async (save: boolean = true, markAsDisabled: boolean = false) => {
    if (nextStep && validateForm()) {
      // set contracting step to valid
      const contractingUdpated = setActualStepToValid();

      if (save) {
        // save contracting to db
        let response = await saveContracting(
          contractingUdpated,
          Contracting_UpdateContracting
        );

        if (isSuccessStatus(response)) {
          // setting contracting id
          if (!contractingState.progress.contractingId && response.data) {
            contractingDispatch(setContractingId(response.data?.result));
          }
          // set step to disabled
          setActualStepToDisabled(markAsDisabled);
          // move to next page (only if success save)
          history.push(nextStep.route);
        } else if (isBadRequestStatus(response)) {
          // contracting no longer editable - show link invalid dialog
          showLinkInvalidDialog(getStateCodeFromValidationProblem(response.data));
        } else {
          // show error dialog
          openInfoDialog("continueErrorTitle", {
            type: AlertType.Danger,
            message: "continueErrorMessage",
            primaryBtn: {
              text: "close",
              action: () => closeInfoDialog(),
            },
          });
        }
      } else {
        // set step to disabled
        setActualStepToDisabled(markAsDisabled);
        // move to next page
        history.push(nextStep.route);
      }
    } else {
      // show validaiton dialog
      showValidationErrorDialog();
    }
  };

  const prevClickHandler = () => {
    if (prevStep) {
      history.push(prevStep.route);
    }
  };

  const saveClickHandler = async () => {
    let response = await saveContracting(
      contractingState.progress,
      Contracting_UpdateContracting,
      true
    );

    if (isSuccessStatus(response)) {
      // show success
      openInfoDialog("saveTitle", {
        type: AlertType.Success,
        message: "saveSuccessMessage",
        primaryBtn: {
          text: "continue",
          action: () => closeInfoDialog(),
        },
      });
    } else if (isBadRequestStatus(response)) {
      // contracting no longer editable - show link invalid dialog
      showLinkInvalidDialog(getStateCodeFromValidationProblem(response.data));
    } else {
      // show error dialog
      openInfoDialog("saveTitle", {
        type: AlertType.Danger,
        message: "saveErrorMessage",
        primaryBtn: {
          text: "saveLater",
          action: () => closeInfoDialog(),
        },
      });
    }
  };

  const finishClickHandler = async (showLoginBtn?: boolean) => {
    if (validateForm()) {
      // set contracting step to valid
      const contractingUdpated = setActualStepToValid();

      // save contracting to db
      let response = await saveContracting(
        contractingUdpated,
        Contracting_FinishContracting
      );

      if (isSuccessStatus(response)) {
        let secondaryBtn: ContractingDialogButtonProps =  {
          text: "login",
          action: () => backToLoginClickHandler()
        };

        let primaryBtn: ContractingDialogButtonProps;
        // logged user
        if (userState.user) {
          primaryBtn = {
            text: "contractingOverview",
            action: () => {
              history.push("/contracting");
              closeInfoDialog();
            }
          };
        }
        // new (anonymous) user
        else {
          primaryBtn = {
            text: "newContracting",
            action: () => redirectToUrl("contracting/new")
          };
        }

        // show success
        openInfoDialog("finishTitle", {
          type: AlertType.Success,
          message: "finishSuccessMessage",
          primaryBtn: primaryBtn,
          secondaryBtn: showLoginBtn ? secondaryBtn : undefined
        });
      } else if (isBadRequestStatus(response)) {
        // contracting no longer editable - show link invalid dialog
        showLinkInvalidDialog(getStateCodeFromValidationProblem(response.data));
      } else {
        // show error dialog
        openInfoDialog("finishTitle", {
          type: AlertType.Danger,
          message: "finishErrorMessage",
          primaryBtn: {
            text: "close",
            action: () => closeInfoDialog(),
          },
        });
      }
    } else {
      showValidationErrorDialog();
    }
  };

  const backToLoginClickHandler = () => { 
    UserManager.signinRedirect();
  }

  //#endregion handlers

  //#region helpers
  const saveContracting = async (
    contracting: ContractingDTO,
    url: string,
    sendEmail: boolean = false
  ): Promise<PostResponse<ActionResultDTO<string>>> => {
    const response = await trackPromise(
      contractingRepository.post<ActionResultDTO<string>>({
        url: url,
        data: contracting,
        params: sendEmail
          ? getQueryParams("SendEmail", String(sendEmail))
          : undefined,
      })
    );

    return response;
  };

  const setActualStepToValid = (): ContractingDTO => {
    // set contracting step to valid
    contractingDispatch(
      setContractingStepValid({ key: actualStep.component, value: true })
    );
    const contractingUdpated = produce(contractingState.progress, (draft) => {
      draft.steps[actualStep.component].valid = true;
    });

    return contractingUdpated;
  };

  const setActualStepToDisabled = (markAsDisabled: boolean) => { 
    if (markAsDisabled) {
      // mark step as disabled
      contractingDispatch(
        setContractingStepDisabled({ key: actualStep.component, value: true })
      );
    }
  }

  const showValidationErrorDialog = () => { 
    openInfoDialog("validationErrorTitle", {
      type: AlertType.Danger,
      message: "validationErrorMessage",
      primaryBtn: {
        text: "checkForm",
        action: () => closeInfoDialog(),
      },
    });
  }

  const showLinkInvalidDialog = (stateCode?: string) => {
    const messageKey = getInvalidLinkMessageKeyFromStateCode(stateCode);
    openInfoDialog("linkInvalidTitle", {
      type: AlertType.Warning,
      message: messageKey,
      primaryBtn: {
        text: "newContracting",
        action: () => redirectToUrl("contracting/new"),
      },
    });
  }
  //#endregion //#region helpers

  //#region return
  return {
    onNextClick: nextClickHandler,
    onPrevClick: prevClickHandler,
    onSaveClick: saveClickHandler,
    onFinishClick: finishClickHandler,
    onBackToLoginClick: backToLoginClickHandler
  };
  //#endregion return
};
